/**
 * Managed Agents — per-brand agent activation, schedules, runs and PPTX
 * templates. Agency employee surface (Bearer = agency `emp_` key).
 *
 * Backend contract (central-api Modules/ClientAgents — consume exactly, do
 * NOT change it; shapes mirror MANAGED_AGENTS_CONTRACT §1.5/§1.6):
 *
 *   GET  shops/{slug}/agents                       [portal.reports.review]
 *        → { data: { agents: AgentView[], available_types: TypeView[] } }
 *   POST shops/{slug}/agents                       [portal.settings.manage]
 *        {agent_type, schedule?{cron,timezone}, budget_cents?}
 *        → 201 { data: AgentView }   (created ACTIVE — the deck first-run lesson)
 *   PATCH shops/{slug}/agents/{agent_type}         [portal.settings.manage]
 *        {schedule?{cron,timezone}, budget_cents?, is_active?}
 *        → { data: AgentView }
 *   DELETE shops/{slug}/agents/{agent_type}        [portal.settings.manage]
 *        → { data: { deactivated: true } }
 *   POST shops/{slug}/agents/{agent_type}/run      [portal.settings.manage]
 *        → 202 { data: { run_id } }   (409 run already in flight, 422 !is_active)
 *
 *   GET  shops/{slug}/agents/{type}/settings       [portal.settings.manage]
 *        → { data: AgentSettingsView }   (Menu A — CONTRACT SettingsView verbatim)
 *   PUT  shops/{slug}/agents/{type}/settings       [portal.settings.manage]
 *        {custom_instructions?, model?(allow-list id), prebuilt_skills?[], apply_to_agency?}
 *        → { data: AgentSettingsView, reprovisioned, deployments_updated, deployments_failed }
 *        (422 on a bad model id / over-long instructions / unknown skill key)
 *
 *   GET  shops/{slug}/agent-runs?agent_type=&limit=&before=<iso>
 *        [portal.reports.review] → { data: RunView[], meta: { has_more } }
 *        limit default 20, max 100 — always sent explicitly.
 *   GET  agent-runs/{id}                           [portal.reports.review]
 *        → { data: RunView }
 *   GET  agent-runs/{id}/artifact?kind=pptx|xlsx   [portal.reports.review]
 *        → { data: { url, expires_at } }  (15-min Spaces URL; the rescue path
 *        answers { url } pointing at the same-auth streaming route instead)
 *
 *   GET  shops/{slug}/agent-template               [portal.reports.review]
 *        → { data: { current, agency_default, effective, versions } }
 *   POST shops/{slug}/agent-template               [portal.settings.manage]
 *        multipart field `template` (.pptx/.potx) → 201 { data: TemplateView }
 *   DELETE shops/{slug}/agent-template             [portal.settings.manage]
 *        (falls back to the agency default)
 *   GET/POST/DELETE agency/agent-template          [portal.settings.manage]
 *        — the agency-level default template (shop_id null).
 */
import { request, requestMultipart, toQuery } from './client-reports-http';

// ---------- wire types (CONTRACT §1.6 — verbatim) ----------

/**
 * Registry-driven — 'client_reporting' | 'google_sheets' today, but new agent
 * types must flow through with NO frontend change, so the wire type stays
 * `string` and the UI renders whatever `available_types` advertises.
 */
export type AgentTypeId = string;

export type AgentRunStatus =
  | 'scheduled'
  | 'running'
  | 'generating'
  | 'ready'
  | 'failed'
  | 'budget_reached';

export type AgentRunTrigger = 'schedule' | 'manual';

/**
 * Stored run artifacts: `pptx`/`xlsx` are downloads; `html` is a findings-mode
 * scorecard (Agents v3) opened inline through {@link getRunOutputHtml} — a
 * client-report run stores no html artifact (its HTML lives in the workbench).
 */
export type AgentArtifactKind = 'pptx' | 'xlsx' | 'html';

/** `findings` = the findings.json a findings-mode agent writes (never a download). */
export type AgentOutputKind = 'html' | 'pptx' | 'xlsx' | 'findings';

/** How a type's outputs land (Agents v3, D10). */
export type AgentOutputMode = 'client_report' | 'findings' | 'workbook';

/** findings.json `status` as parsed onto the run (Agents v3, D11). */
export type AgentFindingsStatus = 'ok' | 'findings' | 'failed' | 'needs_setup';

export interface AgentSchedule {
  /** 5-field POSIX cron. */
  cron: string;
  /** IANA timezone name, e.g. 'Europe/Amsterdam'. */
  timezone: string;
}

export interface AgentNeedsAttention {
  at: string;
  /** Plain-language sentence, e.g. "Runs are paused after repeated failures — AdUp has been notified." */
  reason: string;
}

export interface AgentTemplateRef {
  version: number;
  filename: string;
  source: 'client' | 'agency';
}

/** The period a schedule-cadence "Run now" would report if clicked now. */
export interface AgentRunNowPeriod {
  unit: string;
  label: string;
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD */
  end: string;
  days: number;
}

/** The period a run without a recorded period is expected to cover. */
export interface AgentRunExpectedPeriod {
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD */
  end: string;
  label: string | null;
}

export interface AgentView {
  agent_type: AgentTypeId;
  label: string;
  is_active: boolean;
  schedule: AgentSchedule;
  /** Per-run cap in cents; null = the config default applies. */
  budget_cents: number | null;
  /** What a run actually gets (budget_cents ?? config default). */
  effective_budget_cents: number;
  outputs: AgentOutputKind[];
  needs_attention: AgentNeedsAttention | null;
  next_run_at: string | null;
  last_run_at: string | null;
  /** What "Run now" reports for the saved schedule. Absent on older APIs. */
  run_now_period?: AgentRunNowPeriod | null;
  template: AgentTemplateRef | null;
  /** Only sent to a member who may review reports. */
  worker_employee_id?: string;
  created_at: string;
}

export interface AgentRunArtifact {
  kind: AgentArtifactKind;
  filename: string;
  size_bytes: number;
}

export interface RunView {
  id: string;
  agent_type: AgentTypeId;
  trigger: AgentRunTrigger;
  status: AgentRunStatus;
  period_start: string | null;
  period_end: string | null;
  period_label: string | null;
  /**
   * Set while the run has no recorded period yet (it is still running, or an
   * ad-hoc period was asked for). Absent on older APIs.
   */
  expected_period?: AgentRunExpectedPeriod | null;
  /** The ingested HTML report (open in the report workbench). */
  client_report_id: string | null;
  artifacts: AgentRunArtifact[];
  summary: string | null;
  /** Findings-mode runs only (Agents v3); null / [] otherwise. Absent on older APIs. */
  findings_status?: AgentFindingsStatus | null;
  findings?: string[];
  cost_cents: number | null;
  error_code: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/** 'scheduled' → the schedule/run UI; 'chat' → the Assistant chat panel (Menu C). */
export type AgentKind = 'scheduled' | 'chat';

export interface TypeView {
  agent_type: AgentTypeId;
  label: string;
  /** 'scheduled' (default) or 'chat' (the Assistant). */
  kind: AgentKind;
  outputs: AgentOutputKind[];
  default_schedule: AgentSchedule;
  /**
   * True when default_schedule is the brand's own last schedule (from a
   * switched-off agent) rather than the registry default. Absent on older APIs.
   */
  remembered_schedule?: boolean;
  default_budget_cents: number;
  /**
   * Up to three short "what this skill can do" lines from the registry.
   * Absent on older APIs — treat as [].
   */
  capabilities?: string[];
  /**
   * Card copy from the registry (Agents hub): an uppercase category eyebrow
   * ("Reporting"), a one-line tagline and a longer description. Absent on
   * older APIs — `lib/agents/agent-type-meta.ts` fills the gap.
   */
  category?: string;
  tagline?: string;
  description?: string;
  /**
   * The cadences the schedule picker offers for this type (Agents v3). Chat
   * types carry []; absent on older APIs — treat as ['weekly', 'monthly'].
   */
  allowed_cadences?: AgentCadence[];
  /** Absent on older APIs — treat as 'client_report' when outputs include html. */
  output_mode?: AgentOutputMode;
  /** Agents v3 (D12): an agency-authored type — editable / deletable on the hub. */
  custom?: boolean;
  definition_id?: string | null;
}

/** Schedule cadence vocabulary of the picker (composed into a 5-field cron). */
export type AgentCadence = 'daily' | 'weekly' | 'monthly';

export interface TemplateView {
  id: string;
  version: number;
  filename: string;
  size_bytes: number;
  uploaded_by_name: string | null;
  uploaded_at: string;
  shop_scope: 'client' | 'agency';
}

// ---------- request/response envelopes ----------

export interface AgentsIndex {
  agents: AgentView[];
  available_types: TypeView[];
}

export interface ActivateAgentBody {
  agent_type: AgentTypeId;
  schedule?: AgentSchedule;
  budget_cents?: number;
}

export interface UpdateAgentBody {
  schedule?: AgentSchedule;
  budget_cents?: number | null;
  is_active?: boolean;
}

export interface ListAgentRunsParams {
  agent_type?: AgentTypeId;
  /** Rows to return. Clamped to 1…{@link AGENT_RUNS_MAX_LIMIT} before sending. */
  limit?: number;
  /** ISO cursor — rows created strictly before this moment. */
  before?: string;
}

export interface AgentRunsMeta {
  has_more: boolean;
}

export interface AgentRunPage {
  data: RunView[];
  meta: AgentRunsMeta;
}

export interface AgentRunArtifactUrl {
  url: string;
  /** Absent on the streaming-route rescue (same-auth download endpoint). */
  expires_at?: string | null;
}

/** Per-brand template state — the client template plus the agency fallback. */
export interface ShopTemplateState {
  current: TemplateView | null;
  agency_default: TemplateView | null;
  /** Which template a run would actually use right now. */
  effective: 'client' | 'agency' | null;
  versions: TemplateView[];
}

/** Agency-level default template state (shop_id null scope). */
export interface AgencyTemplateState {
  current: TemplateView | null;
  versions: TemplateView[];
}

/** Server-side caps (ClientAgents run listing). */
export const AGENT_RUNS_DEFAULT_LIMIT = 20;
export const AGENT_RUNS_MAX_LIMIT = 100;

// ---------- agents ----------

/** Everything the Agents page needs in one call: active rows + the registry. */
export async function listAgents(slug: string): Promise<AgentsIndex> {
  const res = await request<{ data?: Partial<AgentsIndex> }>(
    `shops/${encodeURIComponent(slug)}/agents`,
    { method: 'GET' }
  );
  return {
    agents: Array.isArray(res.data?.agents)
      ? (res.data!.agents as AgentView[])
      : [],
    available_types: Array.isArray(res.data?.available_types)
      ? (res.data!.available_types as TypeView[])
      : [],
  };
}

/**
 * Activate an agent type for the brand. Schedule/budget are optional — the
 * server applies the type defaults — and the row comes back ACTIVE
 * immediately, so the toggle can trust the response.
 */
export async function activateAgent(
  slug: string,
  body: ActivateAgentBody
): Promise<AgentView> {
  const res = await request<{ data: AgentView }>(
    `shops/${encodeURIComponent(slug)}/agents`,
    { method: 'POST', body: JSON.stringify(body) }
  );
  return res.data;
}

export async function updateAgent(
  slug: string,
  agentType: AgentTypeId,
  body: UpdateAgentBody
): Promise<AgentView> {
  const res = await request<{ data: AgentView }>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(agentType)}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
  return res.data;
}

/**
 * Deactivate = pause + archive the deployment and soft-delete the row.
 * Settings are remembered server-side; re-activating restores them.
 */
export async function deactivateAgent(
  slug: string,
  agentType: AgentTypeId
): Promise<{ deactivated: boolean }> {
  const res = await request<{ data?: { deactivated?: boolean } }>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(agentType)}`,
    { method: 'DELETE' }
  );
  return { deactivated: res.data?.deactivated === true };
}

/**
 * Queue a manual run. The API answers 202 with the new run id; 409 when a run
 * is already in flight and 422 when the agent is switched off — the page
 * mirrors both refusals via `agentRunBlocker()` before ever calling this.
 */
export async function runAgentNow(
  slug: string,
  agentType: AgentTypeId
): Promise<{ run_id: string }> {
  const res = await request<{ data: { run_id: string } }>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(
      agentType
    )}/run`,
    { method: 'POST' }
  );
  return res.data;
}

// ---------- runs ----------

/**
 * One page of run history for the brand, newest first. The limit is always
 * sent explicitly (deck-module lesson: an implicit default silently truncates
 * the "is anything in flight?" view). `before` pages further back.
 */
export async function listAgentRuns(
  slug: string,
  params: ListAgentRunsParams = {}
): Promise<AgentRunPage> {
  const requested = Math.trunc(
    Number(params.limit ?? AGENT_RUNS_DEFAULT_LIMIT)
  );
  const limit = Number.isFinite(requested)
    ? Math.min(Math.max(requested, 1), AGENT_RUNS_MAX_LIMIT)
    : AGENT_RUNS_DEFAULT_LIMIT;

  const query = toQuery({
    agent_type: params.agent_type,
    limit,
    before: params.before,
  });

  const res = await request<{
    data?: RunView[];
    meta?: Partial<AgentRunsMeta>;
  }>(`shops/${encodeURIComponent(slug)}/agent-runs${query}`, {
    method: 'GET',
  });

  return {
    data: Array.isArray(res.data) ? res.data : [],
    meta: { has_more: res.meta?.has_more === true },
  };
}

export async function getAgentRun(id: string): Promise<RunView> {
  const res = await request<{ data: RunView }>(
    `agent-runs/${encodeURIComponent(id)}`,
    { method: 'GET' }
  );
  return res.data;
}

/**
 * Short-lived download URL for a run's PPTX/XLSX artifact. Fetch this on
 * click, then open the returned URL (anchor/window.open) — the bearer never
 * goes into a URL; the rescue streaming route authenticates via the session
 * cookie instead.
 */
export async function getRunArtifactUrl(
  runId: string,
  kind: AgentArtifactKind
): Promise<AgentRunArtifactUrl> {
  const res = await request<{ data: AgentRunArtifactUrl }>(
    `agent-runs/${encodeURIComponent(runId)}/artifact${toQuery({ kind })}`,
    { method: 'GET' }
  );
  return res.data;
}

/** GET agent-runs/{id}/output — a findings-mode run's stored HTML, inline. */
export interface AgentRunOutputHtml {
  run_id: string;
  filename: string;
  size_bytes: number;
  html: string;
}

/**
 * The stored HTML scorecard of a findings-mode run, as a string for a
 * sandboxed `srcdoc` viewer. 404 `output_not_found` when the run stored none
 * (a client-report run, or a needs_setup run).
 */
export async function getRunOutputHtml(runId: string): Promise<AgentRunOutputHtml> {
  const res = await request<{ data: AgentRunOutputHtml }>(
    `agent-runs/${encodeURIComponent(runId)}/output`,
    { method: 'GET' }
  );
  return res.data;
}

// ---------- templates (per-brand) ----------

export async function getTemplate(slug: string): Promise<ShopTemplateState> {
  const res = await request<{ data?: Partial<ShopTemplateState> }>(
    `shops/${encodeURIComponent(slug)}/agent-template`,
    { method: 'GET' }
  );
  return {
    current: res.data?.current ?? null,
    agency_default: res.data?.agency_default ?? null,
    effective: res.data?.effective ?? null,
    versions: Array.isArray(res.data?.versions)
      ? (res.data!.versions as TemplateView[])
      : [],
  };
}

/**
 * Upload this brand's .pptx/.potx template. Multipart — the browser sets the
 * boundary; the backend expects the file under the `template` field. Active
 * agents' deployments are re-pointed server-side; past runs keep the version
 * they were stamped with.
 */
export async function uploadTemplate(
  slug: string,
  file: File
): Promise<TemplateView> {
  const form = new FormData();
  form.append('template', file);
  const res = await requestMultipart<{ data: TemplateView }>(
    `shops/${encodeURIComponent(slug)}/agent-template`,
    form
  );
  return res.data;
}

/** Remove the brand template — runs fall back to the agency default. */
export async function deleteTemplate(slug: string): Promise<void> {
  await request<unknown>(`shops/${encodeURIComponent(slug)}/agent-template`, {
    method: 'DELETE',
  });
}

// ---------- templates (agency-level default) ----------

export async function getAgencyTemplate(): Promise<AgencyTemplateState> {
  const res = await request<{ data?: Partial<AgencyTemplateState> }>(
    'agency/agent-template',
    { method: 'GET' }
  );
  return {
    current: res.data?.current ?? null,
    versions: Array.isArray(res.data?.versions)
      ? (res.data!.versions as TemplateView[])
      : [],
  };
}

export async function uploadAgencyTemplate(file: File): Promise<TemplateView> {
  const form = new FormData();
  form.append('template', file);
  const res = await requestMultipart<{ data: TemplateView }>(
    'agency/agent-template',
    form
  );
  return res.data;
}

export async function deleteAgencyTemplate(): Promise<void> {
  await request<unknown>('agency/agent-template', { method: 'DELETE' });
}

// ---------- settings (Menu A — CONTRACT SettingsView, verbatim) ----------

/**
 * Model-tier keys the picker offers. `'custom'` is display-only: it marks an
 * effective model that AdUp set outside the standard allow-list, so no segment
 * is pre-selected and picking one of the three moves the brand onto a tier.
 */
export type AgentModelTier = 'balanced' | 'advanced' | 'fast' | 'custom';

/** Where the effective config resolves from: registry / agency row / brand row. */
export type AgentSettingsSource = 'default' | 'agency' | 'brand';

/** One selectable model in the allow-list picker (`options.models`). */
export interface AgentModelOption {
  id: string;
  tier: AgentModelTier;
  label: string;
  hint: string;
}

/** One prebuilt-skill row the menu offers for this type (`options.prebuilt_skills`). */
export interface AgentPrebuiltSkillOption {
  id: string;
  label: string;
  desc: string;
  /** In the type's registry default — pre-selected when no override exists. */
  default_on: boolean;
}

/**
 * A custom (agency-uploaded) skill option offered in the Skills menu
 * (`options.custom_skills`). `id` is the LOCAL catalog id the PUT sends in
 * `custom_skill_ids`; `source` is `'agency'` (this org uploaded it, editable in
 * Menu B) or `'adup'` (a read-only house skill).
 */
export interface AgentCustomSkillOption {
  id: string;
  name: string;
  description: string | null;
  source: string;
}

/** The config in force for the brand right now (defaults ⊕ agency ⊕ brand). */
export interface AgentSettingsEffective {
  /** '' when none. */
  custom_instructions: string;
  /** The allow-list id actually in force. */
  model: string;
  model_tier: AgentModelTier;
  /** Catalog keys in force. */
  prebuilt_skills: string[];
  /** LOCAL custom-skill ids in force (subset of options.custom_skills ids). */
  custom_skill_ids: string[];
}

export interface AgentSettingsOptions {
  /** The allow-list, for the model picker. */
  models: AgentModelOption[];
  /** The prebuilt-skill catalog for this type. */
  prebuilt_skills: AgentPrebuiltSkillOption[];
  /** Phase 2 — always [] for now. */
  custom_skills: AgentCustomSkillOption[];
}

/** GET/PUT payload — mirrors CONTRACT `SettingsView`. */
export interface AgentSettingsView {
  agent_type: AgentTypeId;
  label: string;
  /** Where the effective config comes from. */
  source: AgentSettingsSource;
  effective: AgentSettingsEffective;
  options: AgentSettingsOptions;
  /** Instruction character budget (e.g. 4000). */
  instructions_max: number;
  /** Read-only "Data & tools" chips (from the shop's MCP profile; [] if none). */
  connected_platforms: string[];
}

/**
 * PUT body. Every field is optional on the wire — the menu sends the full
 * editable set each save. `model` must be an allow-list id; `prebuilt_skills`
 * a subset of the catalog keys; `custom_instructions` ≤ `instructions_max`
 * (the server 422s otherwise). `apply_to_agency:true` upserts the agency-wide
 * default AND clears this brand's own override (the brand then inherits it);
 * false/absent upserts just this brand's row.
 */
export interface UpdateAgentSettingsBody {
  custom_instructions?: string;
  model?: string;
  prebuilt_skills?: string[];
  /** LOCAL custom-skill ids to attach (subset of the org catalog). */
  custom_skill_ids?: string[];
  apply_to_agency?: boolean;
}

/** PUT response — the fresh view plus what the re-provision/re-point touched. */
export interface UpdateAgentSettingsResult {
  data: AgentSettingsView;
  /** Whether a (re-)provision of the config-hashed agent actually happened. */
  reprovisioned: boolean;
  /** Active deployments successfully re-pointed at the resolved agent version. */
  deployments_updated: number;
  /** Active deployments whose re-point failed (best-effort; AdUp is notified). */
  deployments_failed: number;
}

/** Menu A — the effective config + the picker options for one agent type. */
export async function getAgentSettings(
  slug: string,
  agentType: AgentTypeId
): Promise<AgentSettingsView> {
  const res = await request<{ data: AgentSettingsView }>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(
      agentType
    )}/settings`,
    { method: 'GET' }
  );
  return res.data;
}

/**
 * Save the brand's (or, with `apply_to_agency`, the agency-wide) customization.
 * Re-provisions the config-hashed agent and re-points active deployments
 * server-side; the numeric siblings are read defensively so a sparse envelope
 * never throws a render.
 */
export async function updateAgentSettings(
  slug: string,
  agentType: AgentTypeId,
  body: UpdateAgentSettingsBody
): Promise<UpdateAgentSettingsResult> {
  const res = await request<Partial<UpdateAgentSettingsResult>>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(
      agentType
    )}/settings`,
    { method: 'PUT', body: JSON.stringify(body) }
  );
  const updated = Number(res.deployments_updated);
  const failed = Number(res.deployments_failed);
  return {
    data: res.data as AgentSettingsView,
    reprovisioned: res.reprovisioned === true,
    deployments_updated: Number.isFinite(updated) ? updated : 0,
    deployments_failed: Number.isFinite(failed) ? failed : 0,
  };
}

// ---------- custom skills (Menu B — Anthropic Agent Skills catalog) ----------

/**
 * One row in the agency's custom-skill catalog. `source` is `'agency'` (this
 * org uploaded it — deletable in Menu B) or `'adup'` (a read-only house skill
 * AdUp published to every agency). These are the Anthropic **Agent Skills**
 * (the agent's uploaded know-how), distinct from the Claude-Code org skills
 * library.
 */
export interface CustomSkill {
  id: string;
  name: string;
  description: string | null;
  source: string;
  created_at: string | null;
  /**
   * The scheduled agent types this skill is used by at AGENCY level (Agents
   * v3 — the Skills tab's "used by" toggles). Absent on older APIs.
   */
  agent_types?: AgentTypeId[];
  has_package?: boolean;
  updated_at?: string | null;
}

/** PUT …/custom-skills/{id}/assignments response — the fresh row + apply tallies. */
export interface CustomSkillAssignmentsResult {
  data: CustomSkill;
  deployments_updated: number;
  deployments_failed: number;
}

/**
 * Set EXACTLY which scheduled agent types a skill is used by, agency-wide
 * (every other type is detached). Active deployments the change touches are
 * re-pointed server-side.
 */
export async function setCustomSkillAssignments(
  slug: string,
  id: string,
  agentTypes: AgentTypeId[]
): Promise<CustomSkillAssignmentsResult> {
  const res = await request<Partial<CustomSkillAssignmentsResult>>(
    `shops/${encodeURIComponent(slug)}/agents/custom-skills/${encodeURIComponent(
      id
    )}/assignments`,
    { method: 'PUT', body: JSON.stringify({ agent_types: agentTypes }) }
  );
  const updated = Number(res.deployments_updated);
  const failed = Number(res.deployments_failed);
  return {
    data: res.data as CustomSkill,
    deployments_updated: Number.isFinite(updated) ? updated : 0,
    deployments_failed: Number.isFinite(failed) ? failed : 0,
  };
}

/** Fields for a new custom-skill upload. `file` is a `.zip` containing SKILL.md. */
export interface UploadCustomSkillBody {
  name: string;
  description?: string;
  file: File;
}

/** The org's custom-skill catalog (its agency skills + AdUp house skills). */
export async function listCustomSkills(slug: string): Promise<CustomSkill[]> {
  const res = await request<{ data?: CustomSkill[] }>(
    `shops/${encodeURIComponent(slug)}/agents/custom-skills`,
    { method: 'GET' }
  );
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Upload a `.zip` (a folder containing SKILL.md) → Anthropic's Skills API → a
 * new catalog row. The server validates the package (real zip + a root
 * SKILL.md, under the size cap) and 422s otherwise.
 */
export async function uploadCustomSkill(
  slug: string,
  body: UploadCustomSkillBody
): Promise<CustomSkill> {
  const form = new FormData();
  form.append('name', body.name);
  if (body.description) form.append('description', body.description);
  form.append('file', body.file);
  const res = await requestMultipart<{ data: CustomSkill }>(
    `shops/${encodeURIComponent(slug)}/agents/custom-skills`,
    form
  );
  return res.data;
}

/**
 * Archive an AGENCY custom skill this org owns (house skills are read-only).
 * The catalog + the settings menu stop offering it; agents that referenced it
 * are cleaned up on their next provision.
 */
export async function deleteCustomSkill(
  slug: string,
  id: string
): Promise<void> {
  await request<unknown>(
    `shops/${encodeURIComponent(slug)}/agents/custom-skills/${encodeURIComponent(
      id
    )}`,
    { method: 'DELETE' }
  );
}

// ---------- custom agents (Agents v3 — "Build your own agent") ----------

export type CustomAgentCategory = 'Reporting' | 'Analysis' | 'Optimization' | 'Monitoring' | 'Creative';

/** The builder's proposal = central-api's create/patch body (validated server-side). */
export interface CustomAgentBlueprint {
  name: string;
  tagline?: string;
  category: CustomAgentCategory | string;
  description?: string;
  output_mode: AgentOutputMode;
  schedule: {
    cadence: AgentCadence;
    /** weekly 0–6 (Sun–Sat); monthly 1–28; daily 0. */
    day: number;
    hour: number;
    minute: number;
    timezone?: string;
  };
  platforms: string[];
  focus_metrics: string[];
  capabilities: string[];
  /** The agent's main prompt (40–12000 chars). */
  instructions: string;
  budget_cents?: number;
  /**
   * The wizard answers the blueprint was built from (stored with it, so
   * editing reopens the wizard pre-filled). Free-form: the wizard's own shape.
   */
  intake?: unknown;
}

export interface CustomAgentDefinitionView {
  id: string;
  agent_type: AgentTypeId;
  name: string;
  tagline: string | null;
  category: string;
  description: string | null;
  output_mode: AgentOutputMode;
  outputs: AgentOutputKind[];
  platforms: string[];
  cadence: AgentCadence;
  default_cron: string;
  timezone: string | null;
  kickoff_prompt: string;
  focus_metrics: string[];
  capabilities: string[];
  allow_writes: boolean;
  budget_cents: number | null;
  shop_id: number | null;
  status: 'active' | 'archived';
  blueprint: CustomAgentBlueprint | null;
  estimated_monthly_cents: number;
  created_at: string | null;
  updated_at: string | null;
}

export async function listCustomAgents(): Promise<CustomAgentDefinitionView[]> {
  const res = await request<{ data?: CustomAgentDefinitionView[] }>('agency/custom-agents', {
    method: 'GET',
  });
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Create a custom agent type from a blueprint (422 with a message on a bad one).
 * `clientRequestId` makes the create idempotent: sending the same id again
 * returns the agent the first request created instead of a second one.
 */
export async function createCustomAgent(
  blueprint: CustomAgentBlueprint,
  options: { signal?: AbortSignal; clientRequestId?: string } = {}
): Promise<CustomAgentDefinitionView> {
  const body = options.clientRequestId
    ? { ...blueprint, client_request_id: options.clientRequestId }
    : blueprint;
  const res = await request<{ data: CustomAgentDefinitionView }>('agency/custom-agents', {
    method: 'POST',
    body: JSON.stringify(body),
    signal: options.signal,
  });
  return res.data;
}

export interface UpdateCustomAgentResult {
  data: CustomAgentDefinitionView;
  deployments_updated: number;
  deployments_failed: number;
}

/** Patch a definition with a partial blueprint; active instances are re-pointed server-side. */
export async function updateCustomAgent(
  id: string,
  changes: Partial<CustomAgentBlueprint>,
  options: { signal?: AbortSignal } = {}
): Promise<UpdateCustomAgentResult> {
  const res = await request<Partial<UpdateCustomAgentResult>>(
    `agency/custom-agents/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(changes), signal: options.signal }
  );
  const updated = Number(res.deployments_updated);
  const failed = Number(res.deployments_failed);
  return {
    data: res.data as CustomAgentDefinitionView,
    deployments_updated: Number.isFinite(updated) ? updated : 0,
    deployments_failed: Number.isFinite(failed) ? failed : 0,
  };
}

/** Archive a definition — every brand's instance is switched off. */
export async function deleteCustomAgent(
  id: string
): Promise<{ archived: boolean; deactivated: number; failed: number }> {
  const res = await request<{ data?: { archived?: boolean; deactivated?: number; failed?: number } }>(
    `agency/custom-agents/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
  return {
    archived: res.data?.archived === true,
    deactivated: Number(res.data?.deactivated ?? 0) || 0,
    failed: Number(res.data?.failed ?? 0) || 0,
  };
}

/** The builder handshake: where to stream, the signed token, and the brand context it carries. */
export interface BuilderSession {
  chat_url: string;
  token: string;
  expires_at: number;
  context: {
    shop_name?: string;
    shop_slug?: string;
    connected_platforms?: string[];
    has_kpi_targets?: boolean;
    existing_agents?: { agent_type: string; label: string }[];
    categories?: string[];
    output_modes?: string[];
    cadences?: string[];
    max_prompt_chars?: number;
    today?: string;
    timezone?: string;
    currency?: string;
    [key: string]: unknown;
  };
  /** Edit mode: the custom agent's blueprint as saved, so the builder opens pre-filled. */
  blueprint?: CustomAgentBlueprint | null;
}

/**
 * Mint a builder token for the active brand (503 `builder_not_configured`
 * when the sidecar is not set up on this environment). `definitionId` opens
 * an existing custom agent in edit mode.
 */
export async function startBuilderSession(
  slug: string,
  definitionId?: string
): Promise<BuilderSession> {
  const res = await request<{ data: BuilderSession }>(
    `shops/${encodeURIComponent(slug)}/agents/builder/session`,
    {
      method: 'POST',
      body: JSON.stringify(definitionId ? { definition_id: definitionId } : {}),
    }
  );
  return res.data;
}

// ---------- chat (Menu C — the Assistant) ----------

/**
 * The handshake central-api returns to open a chat: the agent-chat sidecar URL
 * to POST messages to, a short-lived signed token to authorize them, and the
 * per-shop memory store id. The browser holds the token only for the handshake;
 * continuity comes from the session id the sidecar returns in its first frame.
 */
export interface ChatHandshake {
  chat_url: string;
  token: string;
  expires_at: number;
  memory_store_id: string | null;
}

/** Authorize a chat for the brand + the (chat-kind) agent type. */
export async function startChat(
  slug: string,
  agentType: AgentTypeId
): Promise<ChatHandshake> {
  const res = await request<{ data: ChatHandshake }>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(
      agentType
    )}/chat`,
    { method: 'POST', body: '{}' }
  );
  return res.data;
}

/** Clear the brand's chat memory (the next chat starts the memory fresh). */
export async function clearChatMemory(
  slug: string,
  agentType: AgentTypeId
): Promise<void> {
  await request<unknown>(
    `shops/${encodeURIComponent(slug)}/agents/${encodeURIComponent(
      agentType
    )}/chat/memory`,
    { method: 'DELETE' }
  );
}
